import clsx from 'clsx';
import { FC, useState } from 'react';
import { useIntl } from 'react-intl';

import { gramsForMultiplier } from '../utils/quickSaleFormat';

// `Pet.customer_multiplier` only takes these steps (MULTIPLIER_CHOICES).
export const MULTIPLIER_OPTIONS = [
  -50, -40, -30, -20, -10, 0, 10, 20, 30, 40, 50,
];

type Props = {
  grams: number | null;
  calories: number | null;
  baseGrams: number | null;
  baseMultiplier: number;
  multiplier: number;
  changed: boolean;
  onChange: (multiplier: number) => void;
  variant?: 'highlight' | 'inline';
};

/**
 * The recommended daily ration. Grams cannot be typed: the ration follows the
 * pet's calories, and the CRM adjusts it with the customer multiplier.
 */
const DailyRationField: FC<Props> = ({
  grams,
  calories,
  baseGrams,
  baseMultiplier,
  multiplier,
  changed,
  onChange,
  variant = 'highlight',
}) => {
  const intl = useIntl();
  const [editing, setEditing] = useState(false);

  const editor = (
    <select
      className="form-select form-select-sm"
      value={multiplier}
      aria-label={intl.formatMessage({ id: 'quickSale.dailyRation' })}
      onChange={(event) => onChange(Number(event.target.value))}
    >
      {MULTIPLIER_OPTIONS.map((option) => (
        <option key={option} value={option}>
          {option > 0 ? `+${option}` : option}%
          {baseGrams
            ? ` · ${gramsForMultiplier(baseGrams, baseMultiplier, option)} g/día`
            : ''}
        </option>
      ))}
    </select>
  );

  const value = (
    <span className={clsx(changed && 'text-primary')}>
      {grams ? `${grams} g / día` : '-'}
    </span>
  );

  if (variant === 'inline') {
    return (
      <div className="d-flex align-items-center gap-3">
        <strong>{editing ? editor : value}</strong>
        <button
          type="button"
          className="btn btn-icon btn-sm btn-light"
          title={intl.formatMessage({ id: 'quickSale.edit' })}
          onClick={() => setEditing(!editing)}
        >
          <i className="fas fa-pen fs-7" />
        </button>
      </div>
    );
  }

  return (
    <div className="bg-light-primary rounded p-5 mb-5">
      <div className="d-flex justify-content-between align-items-start">
        <span className="fw-bolder text-gray-800">
          {intl.formatMessage({ id: 'quickSale.dailyRation' })}
          <i
            className="fas fa-info-circle ms-2 text-gray-500"
            title={intl.formatMessage({ id: 'quickSale.dailyRationHelp' })}
          />
        </span>
        <button
          type="button"
          className="btn btn-icon btn-sm btn-light"
          title={intl.formatMessage({ id: 'quickSale.edit' })}
          onClick={() => setEditing(!editing)}
        >
          <i className="fas fa-pen fs-7" />
        </button>
      </div>
      <div className="fs-2x fw-bolder mt-2">{value}</div>
      <div className="text-gray-600">
        {calories
          ? `≈ ${intl.formatNumber(Math.round(calories))} kcal / día`
          : ''}
      </div>
      {editing && <div className="mt-3">{editor}</div>}
    </div>
  );
};

export { DailyRationField };
